import React, { useState } from "react";
import { markPaid, unmarkPaid, money } from "@/lib/apiClient";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { Banknote, CheckCircle2, Undo2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const METHODS = [
  ["bank_transfer", "Bank transfer"],
  ["card", "Card"],
  ["cheque", "Cheque"],
  ["other", "Other"],
];

export const PaymentCard = ({ inv, onChanged }) => {
  const { user } = useApp();
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState("bank_transfer");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);

  if (inv.status !== "approved") return null;
  const p = inv.payment;

  const pay = async () => {
    setBusy(true);
    try {
      const updated = await markPaid(inv.id, { paid_at: paidAt, method, reference: reference || null, user });
      toast.success(`Marked as paid — ${money(inv.total, inv.currency)}`);
      onChanged(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark as paid");
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    setBusy(true);
    try {
      const updated = await unmarkPaid(inv.id, user);
      toast.success("Payment reverted");
      onChanged(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revert");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="payment-card">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Banknote className="h-4 w-4 text-emerald-500" /> Payment
        </div>
        {p ? (
          <span data-testid="paid-badge" className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-500">
            <CheckCircle2 className="h-3 w-3" /> Paid {p.paid_at}
          </span>
        ) : (
          <span className="text-xs text-amber-500" data-testid="unpaid-label">Unpaid{inv.due_date ? ` · due ${inv.due_date}` : ""}</span>
        )}
      </div>

      {p ? (
        <div className="flex items-center justify-between gap-3 text-sm">
          <div className="text-xs text-muted-foreground font-mono">
            {METHODS.find(([k]) => k === p.method)?.[1] || p.method}{p.reference ? ` · ${p.reference}` : ""} · by {p.marked_by}
          </div>
          <Button data-testid="undo-paid-btn" variant="ghost" size="sm" onClick={undo} disabled={busy}>
            {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5 mr-1.5" />} Undo
          </Button>
        </div>
      ) : (
        <div className="grid gap-2 sm:grid-cols-[130px_1fr_1fr_auto] items-center">
          <Input data-testid="paid-date-input" type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} className="h-9 text-sm font-mono" />
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger data-testid="paid-method-select" className="h-9 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {METHODS.map(([k, l]) => <SelectItem key={k} value={k} className="text-sm">{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="paid-reference-input" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Reference (optional)" className="h-9 text-sm" />
          <Button data-testid="mark-paid-btn" size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={pay} disabled={busy || !paidAt}>
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Banknote className="h-4 w-4 mr-1.5" />} Mark paid
          </Button>
        </div>
      )}
    </div>
  );
};
